import React, { Component } from 'react';
import styled, {css, keyframes} from 'styled-components';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { sub, format } from 'date-fns'
import {ObjectId} from 'bson'
import { AnimatePresence } from "framer-motion"
import { 
    EditorState, 
    convertFromRaw, 
} from 'draft-js';

import { itemContext } from 'context'
import { Item, Alert } from 'schema'
import { Button, RichText, Animation, Property, Schedule, Dropdown } from 'components'
import { PrioritySelector, ScheduleInput, TagInput, TagBlock, UpgradeModal, SectionSelector } from 'features'
import { FaCalendar, FaExclamation, FaPlus, FaTag } from 'react-icons/fa'

import * as actionCreators from 'store/actions'
import classes from './ItemInput.module.css'
import { scheduleString, formatItemDatetime, momentDate } from 'utils'

const shakeHorizontal = keyframes`
    0%, 100% {transform: translateX(0)}
    10%, 30%, 50%, 70% {transform: translateX(-10px)}
    20%, 40%, 60% {transform: translateX(10px);}
    80% {transform: translateX(8px)}
    90% {transform: translateX(-8px)}
`
const shakeHorizontalAnimation = css`
    ${shakeHorizontal} 0.8s cubic-bezier(0.455, 0.030, 0.515, 0.955) both;
`

const Container = styled.div`
    box-sizing: border-box;
    width: ${props => props.width || 'calc(100% - 2px)'};

    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    /* margin-top: ${props => props.marginTop || '0.5rem'}; */
    margin-left: ${props => props.position && props.offset? (((props.position-props.offset) * 24) + 1) + 'px'
                            : !props.offset ? ((props.position * 24)) + 1 + 'px' 
                            : '1px'};
    animation: ${props => props.error || !props.position_validity ? shakeHorizontalAnimation : 'none'};
    /* overflow-y: hidden; */
    height: 100%;
`

const InputContainer = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: min-content min-content min-content minmax(0, min-content) 1fr auto auto;
    margin-top: ${props => props.marginTop};
`

const Error = styled.div`
    font-size: 0.8rem;
    color: var(--error-color);
    margin-top: 0.2rem;
    :before {
        content: '*'
    }
`


class ItemInput extends Component {
    constructor(props){
        super(props);
        this.ref = React.createRef()
        this.styles = !this.props.styles ? null : this.props.styles === 'secondary' ? 'secondary' : null
        let item = this.props.item || new Item({
            sectionID: this.props.sectionID || this.props.data.pages[this.props.activePageID].sections[0], 
            pageID: this.props.pageID || this.props.defaultPage || this.props.activePageID,
            priority: this.props.priority,
            tags: this.props.tags,
            start_date: this.props.start_date,
            end_date: this.props.end_date
        })

        this.state = {
            open: ['Modal','Input','Edit'].includes(this.props.type) || this.props.inputID === this.props.id ? true : false,
            valid: false,
            pageError: false,
            item: item,
            // position: this.props.parents ? Object.values(this.props.parents).length : 0,
            position: 0,
            position_validity: true,
            renderProps: !this.props.disableProps,
            error: null
        }
        this.keyRules = {
            DEFAULT : {
                'ENTER': () => (
                    this.onSubmitHandler()), 
                'ESC': () => (
                    this.onCancelHandler()), 
                'TAB': () => this.tabHandler('TAB'), 
                'SHIFT_TAB': () => this.tabHandler('SHIFT_TAB')
            },
            SCHEDULE: {
                'ENTER': () => {
                    this.onSubmitHandler();
                    this.props.closeHandler();
                }, 
                'ESC': () => (
                    this.props.cancelHandler()), 
            }
        }
        this.buttonStyle = {
            color:'var(--secondary-accent-color)',
            borderColor:'var(--secondary-accent-color)',
            margin:'0 0.25rem',
            padding: '0.4rem',
            fontSize: '0.7rem',
            // hoverColor: 'var(--secondary-accent-color)',
            boxShadowColor: 'transparent'
        }
        this.submitButtonStyle = {
            backgroundColor:'var(--focus-color)',
            margin:'0',
            fontWeight: 'bolder',
            color: 'white',
            hoverColor: 'white'
            // borderColor: 'var(--primary-accent2-color)'
        }

        this.pages = {}
        const inboxID = this.props.user['inboxID'] || this.props.user['activePages'][0]
        this.pages[inboxID] = this.props.data['pages'][inboxID]['page_name']
        Object.values(this.props.data['pages']).map(page => {
            if(this.props.user['activePages'].includes(page['pageID'])){
                this.pages[page['pageID']] = page.page_name
            }
        })
    }

    static getDerivedStateFromProps(props, state) {
        if(props.type !== 'Edit'){
            state.item.parents = !props.parents ? []: props.parents
        }

        // if(props.item){
        //     state.item = props.item
        // }
        return state
    }

    _getProperties = () => {
        let properties = []
        let gotSchedule = false
        let gotTags= false
        // Dates
        const itemDates = {
            start_date: this.props.item && this.props.item['start_date'] || this.state.item['start_date'],
            end_date: this.props.item && this.props.item['end_date'] || this.state.item['end_date'],
            start_time: this.props.item && this.props.item['start_time'] || this.state.item['start_time'],
            end_time: this.props.item && this.props.item['end_time'] || this.state.item['end_time']
        }
        const schedule = scheduleString(itemDates, this.props.user['timezone']);
        if(schedule.valid){
            properties.push(<Property icon={<FaCalendar/>} tooltip={this.props.itemID === this.props.inputID && 'Schedule'}
                label={<Schedule dates={{start: schedule.start, end: schedule.end}}/>}/>)
            gotSchedule = true
        }

        // Tags
        const tags = this.props.data['tags']
        const tagIDs = this.state.item.tags
        if(tagIDs){
            gotTags = true
            Object.values(tagIDs).map(tagID => {
                const tag = tags[tagID]
                tag && properties.push(<TagBlock label={tag.content} tag={tag} style={{margin: '0.2rem 0.2rem 0.2rem 0',backgroundColor: tag['color']}} onDelete={this.deleteTagHandler}/>)
            })
        }
        return {blocks: properties, gotSchedule: gotSchedule, gotTags: gotTags}
    }

    tabHandler = (keypress) => {
        if(!this.props.enableTab) { return }
        let valid
        const offset = this.props.offset || 0
        const upper = this.props.parents.length - offset // 
        const lower = 0
        let newPosition = this.state.position

        newPosition = keypress === 'TAB' ? this.state.position + 1
                    : keypress === 'SHIFT_TAB' ? this.state.position - 1
                    : this.state.position
        valid = (newPosition >= lower) & (newPosition <= upper)

        this.setState({
            position_validity: valid,
            position: valid ? newPosition : this.state.position,
            error: null
        })
    }

    onOpenHandler = () => {
        this.props.setInput(this.props.id)
        this.setState({
            open: true, 
            position: 0, 
            position_validity: true,
            error: null,
            item: this.props.item || this.state.item || new Item({sectionID: this.props.sectionID, pageID: this.props.pageID || this.props.defaultPage})
        }, () => {
            this.props.onOpen && this.props.onOpen()
        })
    }

    onUpdateHandler = (value, type, richText) => {
        console.log('Iteminput', {value, type})
        // console.log({value,type})
        let updatedItem = {...this.state.item}
        if(type === 'tags'){
            if(Array.isArray(value)){
                updatedItem['tags'] = value
            } else {
                !updatedItem['tags'].includes(value) && updatedItem['tags'].push(value)
            }
        } else if (type === 'page-section') {
            updatedItem['pageID'] = value['pageID']
            updatedItem['sectionID'] = value['sectionID']
        } else {
            updatedItem[type] = value
        }

        if(richText){
            updatedItem['content'] = richText['raw']
        }
        
        this.setState({
            item: updatedItem,
            error: null
        })
    }

    onInputHandler = (data, plainText) => {
        let updatedItem = {...this.state.item}
        updatedItem['content'] = data
        this.setState({
            item: updatedItem,
            valid:  plainText.length > 0 ? true : false,
            error: null
        })
    }

    checkValidity = () => {
        const content = EditorState.createWithContent(convertFromRaw(JSON.parse(this.state.item['content']))).getCurrentContent().getPlainText()
        let valid = true
        let error
        if(content === '' || content.length === 0){
            valid = false
            error = 'Cannot be empty'
        }
        if(!this.state.item['sectionID']) { 
            valid = false 
            error = 'Please select a valid section'
        }
        return { valid, error }
    }

    onSubmitHandler = () => {
        let updatedItem = {...this.state.item}
        const check = this.checkValidity()

        if(!check['valid']){
            this.setState({
                valid: false,
                error: check['error']
            })
        }
        else if(this.state.valid){
            const content = EditorState.createWithContent(convertFromRaw(JSON.parse(this.state.item['content']))).getCurrentContent().getPlainText()
            if(content === '') { return }
            updatedItem = (({ details, item, ...o }) => o)(updatedItem)
            updatedItem['plainText'] = content
            if(this.props.type === 'Edit'){
                this.props.updateItem(updatedItem, this.props.data, this.props.uid, this.props.sessionId)
            } else if (this.props.pageSelector) {
                if(!ObjectId.isValid(this.state.item['pageID'])){ // Check if valid page selected
                    this.setState({pageError: true})
                } else { 
                    this.props.createItem(updatedItem, this.props.data, this.props.uid, this.props.user, this.props.sessionId)
                    this.resetHandler()
                }
            } else {
                updatedItem['parents'] = this.props.parents.slice(0, this.props.offset ? this.state.position + this.props.offset : this.state.position)
                this.props.createItem(updatedItem, this.props.data, this.props.uid, this.props.user, this.props.sessionId)
                this.resetHandler()
            }
            this.setState({
                animation: false
            })
            if(this.props.onClose){
                this.props.onClose()
            }
        }
    }

    setSchedule = (schedule) => {
        let updatedItem = {...this.state.item}
        updatedItem['start_date'] = schedule['startDate']
        updatedItem['end_date'] = schedule['endDate']
        updatedItem['start_time'] = schedule['startTime']
        updatedItem['end_time'] = schedule['endTime']
        updatedItem = formatItemDatetime(updatedItem)
        this.setState({
            item: updatedItem,
            error: null
        })
        if(this.props.id === this.props.inputID & this.props.type === 'Edit'){
            this.updateItemSchedule(schedule)
            // this.props.updateItem(updatedItem, this.props.data, this.props.uid)
        }
    }

    updateSchedule = schedule => {
        let updatedItem = {...this.props.item}
        const hasSchedule = schedule['startDate'] || schedule['endDate'] || schedule['startTime'] || schedule['endTime'] ? true : false
        const refDate = hasSchedule && new Date(schedule['startTime'] || schedule['endTime'] || schedule['startDate'] || schedule['endDate'])

        // Add schedule
        updatedItem['start_date'] = schedule['startDate']
        updatedItem['end_date'] = schedule['endDate']
        updatedItem['start_time'] = schedule['startTime']
        updatedItem['end_time'] = schedule['endTime']

        let updatedAlerts = []
        if(hasSchedule & updatedItem['alerts'].length > 0){
            // Update existing alerts
            Object.values(updatedItem['alerts']).map(alertID => {
                let updatedAlert = this.props.data['alerts'][alertID]
                let option = {}
                option[updatedAlert['reference']['metric']] = updatedAlert['reference']['value']
                const date = sub(new Date(refDate), option)
                const type = date.getHours() === 0 & date.getMinutes() === 0 & date.getSeconds() === 0 & date.getMilliseconds() === 0 ? 'day' : 'time'
                const subtitle = format(date, type === 'day' ? 'eee, MMM d yyyy' : 'eee, MMM d yyy (h:mm aaaa)')

                updatedAlert['date'] = date
                updatedAlert['subtitle'] = subtitle
                updatedAlerts.push(updatedAlert)
            })
        } else if(hasSchedule & updatedItem['alerts'].length === 0) {
            // Create alert
            const date = new Date(refDate)
            const type = date.getHours() === 0 & date.getMinutes() === 0 & date.getSeconds() === 0 & date.getMilliseconds() === 0 ? 'day' : 'time'
            const title = this.props.item.getPlainText()
            const subtitle = format(date, type === 'day' ? 'eee, MMM d yyyy' : 'eee, MMM d yyy (h:mm aaaa)')

            let newAlert = new Alert({
                userID: this.props.uid,
                title: title,
                subtitle: subtitle,
                date: date,
                secID: {id: this.props.item['itemID'], relation: 'item'}
            })
            updatedAlerts.push(newAlert)
        }
        this.props.updateItemSchedule(updatedAlerts, updatedItem, this.props.data, this.props.uid, this.props.sessionId)
    }

    deleteTagHandler = (tag) => {
        let updatedItem = {...this.state.item}
        let tags = updatedItem['tags']
        tags.splice(tags.indexOf(tag['tagID']), 1)
        updatedItem['tags'] = tags
        this.setState({
            item: updatedItem,
            error: null
        })
    }

    resetHandler = () => {
        const pageID = this.state.item['pageID']
        let newItem = new Item({
            sectionID: this.props.sectionID || this.props.data.pages[this.props.activePageID].sections[0], 
            pageID: this.props.pageID || this.props.activePageID, 
            content: '', 
            priority: this.props.priority,
            tags: this.props.tags,
            start_date: this.props.start_date || this.props.schedule && this.props.schedule['start_date'], 
            end_date: this.props.end_time || this.props.schedule && this.props.schedule['end_date'], 
            start_time: this.props.start_date ? null : this.props.schedule && this.props.schedule['start_time'], 
            end_time: this.props.end_date ? null : this.props.schedule && this.props.schedule['end_time']
        })

        this.setState({
            item: newItem,
            valid: false,
            // position: 0,
            position_validity: true,
            pageError: false,
            error: null
        })
        this.props.type === 'Modal' && this.props.setModal(null,null)
    }

    onCancelHandler = () => {
        if(this.props.inputID2){
            this.props.setInput(this.props.inputID, null)
        } else {
            this.resetHandler();
            this.props.type !== 'Input' && this.setState({
                open: false,
            })
            this.props.setInput(null)
            this.props.onClose && this.props.onClose()
        }
        
    }

    selectPageHandler = (pageID) => {
        let updatedItem = {...this.state.item}
        updatedItem['pageID'] = pageID

        // Get default section
        const page = this.props.data['pages'][pageID]
        updatedItem['sectionID'] = page['sections'][0]
        this.props.onSelectPage && this.props.onSelectPage(pageID)
        console.log('[Select page]', {pageID: pageID, item: updatedItem})
        this.setState({
            item: updatedItem,
            pageError: false,
            error: null
        })
    }

    setSection = (pageID, sectionID) => {
        let updatedItem = this.state.item
        updatedItem['sectionID'] = sectionID
        updatedItem['pageID'] = pageID
        this.setState({ item: updatedItem, error: null })
        console.log(updatedItem)
    }

    render() {    
        let properties = null
        if (this.props.id === this.props.inputID || ['Modal','Input','Edit'].includes(this.props.type)){
            properties = this._getProperties()
            const schedule = {
                start_date: this.state.item.start_date ? momentDate(this.state.item.start_date, this.props.user['timezone']) : null,
                end_date: this.state.item.end_date ? momentDate(this.state.item.end_date, this.props.user['timezone']) : null, 
                start_time: this.state.item.start_time ? momentDate(this.state.item.start_time, this.props.user['timezone']) : null,
                end_time: this.state.item.end_time ? momentDate(this.state.item.end_time, this.props.user['timezone']) : null
            }
            const style = this.props.type === 'Edit' ? classes.EditContainer
                        : this.props.styles === 'secondary' ? classes.InputContainer2
                        : classes.InputContainer
            const content = 
            <InputContainer marginTop={properties['blocks'].length === 0 && '0.5rem'}>
                {/* Schedule selector */}
                <Button tooltip='Schedule' icon={<FaCalendar/>} iconOnly styles={{...this.buttonStyle, margin:'0 0.25rem 0 0'}}
                    contextMenu={!this.props.start_date && !this.props.end_date &&
                        <ScheduleInput onSubmit={this.setSchedule} 
                            style={{backgroundColor:'var(--secondary-color)',padding:'0.5rem', width: 'fit-content'}}
                            schedule={schedule}/>}/>

                {/* Priority selector */}
                <Button icon={<FaExclamation/>} iconOnly 
                    tooltip={<div className={classes.Tooltip}><div className={classes.Code}>!</div><div>Priority</div></div>}
                    styles={{...this.buttonStyle,
                        borderColor: itemContext.PRIORITY_COLOR[this.state.item.priority.toUpperCase()],
                        color: itemContext.PRIORITY_COLOR[this.state.item.priority.toUpperCase()],
                        hoverColor: itemContext.PRIORITY_COLOR[this.state.item.priority.toUpperCase()]
                    }}
                    contextMenu={!this.props.priority && <PrioritySelector title='Priority' selected={this.state.item['priority']} onSelect={(value) => this.onUpdateHandler(value,'priority')} styles={{margin: '0.5rem 0'}}/>}
                    />

                {/* Tag selector */}
                <Button icon={<FaTag/>} iconOnly styles={this.buttonStyle} 
                    tooltip={<div className={classes.Tooltip}><div className={classes.Code}>#</div><div>Tag</div></div>}
                    contextMenu={!this.props.tags &&
                        <TagInput disableTags item={this.state.item} styles={{ margin: '0.5rem 0 0 0' }}
                            onSelect={value => this.onUpdateHandler(value, 'tags')} 
                            onCreate={(success) => {this.setState({pageError: !success, error: null})}}/>}/>                                    

                {/* Page selector */}
                {this.props.type === 'Modal' || this.props.sectionSelector ? 
                <Button styles={{backgroundColor: 'var(--secondary-color)', margin: '0 0.3rem', maxWidth: '15rem'}}
                    text={this.props.data.pages[this.state.item['pageID']]['page_name']}
                    tooltip={<div className={classes.Tooltip}><div className={classes.Code}>/</div><div>Page & Section</div></div>}
                    onOpen={() => this.props.setInput(this.props.inputID, 'SectionSelector')}
                    contextMenu={<SectionSelector sectionID={this.state.item['sectionID']} pageID={this.state.item['pageID']}
                                    onSelect={this.setSection} closeOnSelect
                                    // singlePage
                                    styles={{width: '15rem', padding: '0.1rem', margin: '0.5rem',
                                            backgroundColor: 'var(--secondary-color)', boxShadow: 'var(--box-shadow)'}}
                                    />}
                />
                : <div/>}

                <div/>

                {/* Edit button */}
                {this.props.type === 'Edit' ? 
                <Button text='Edit' 
                    onSelect={this.onSubmitHandler} 
                    styles={this.submitButtonStyle} 
                    disable={!this.state.valid}/>
                : <div className={classes.ButtonsContainer}>
                    <Button text='Cancel' onSelect={this.onCancelHandler} styles={{margin:'0 0.25rem 0 0', boxShadowColor: 'transparent', backgroundColor: this.props.styles === 'secondary' ? 'var(--primary-color)' : 'var(--secondary-color)'}}/>
                    <Button text='Create' onSelect={this.onSubmitHandler} styles={this.submitButtonStyle} disable={!this.state.valid}/>
                </div>}
            </InputContainer>
            return (
                <Container key={this.props.key} id={this.props.id} type={this.props.type} error={this.state.error}
                    position={this.state.position} position_validity={this.state.position_validity} ref={this.ref}>
                    <div className={style} style={{overflowY:'hidden'}}>
                        <RichText value={this.state.item.content} item={this.state.item} id={this.props.id}
                            disableAutoUpdate editable={true} edit={this.props.type === 'Edit'} sectionSelector={this.props.type === 'Modal' || this.props.sectionSelector}
                            placeholder={this.props.type !== 'Edit' && 'Add an item...'}
                            onSubmit={this.onSubmitHandler} 
                            onChange={this.onInputHandler} 
                            onProperty={this.onUpdateHandler}
                            keyRules={this.keyRules.DEFAULT} 
                            
                            styles={{
                                primaryColor: this.props.styles === 'secondary' ? 'var(--primary-color)' : 'var(--secondary-color)',
                                secondaryColor: this.props.styles === 'secondary' ? 'var(--secondary-color)' : 'var(--primary-color)',
                                width: '100%',
                                height: 'auto',
                                margin: 0,
                                padding: 0,
                                backgroundColor: 'transparent',
                            }}/>
                        {this.state.renderProps & properties['blocks'].length > 0 ? 
                        <div className={classes.PropertiesContainer}>
                            {properties['blocks']}
                        </div> : null}
                        
                        <AnimatePresence>
                            {!this.props.disableAnimation ? <Animation animation='expand'>{content}</Animation> : content}
                        </AnimatePresence>
                    </div>
                    <AnimatePresence>
                            {this.state.error && 
                            <Animation animation='expand'>
                                <Error>{this.state.error}</Error>
                            </Animation>}
                    </AnimatePresence>
                </Container>
            )
        }       
        else {
            return (
                <div className={classes.InputButton} onClick={this.onOpenHandler}>
                    <div className={this.styles === 'secondary' ? classes.InputButtonContainer2 : classes.InputButtonContainer}>
                        <FaPlus style={{color:'var(--focus-color)', paddingRight:'0.5rem'}}/>
                        <div>Add item</div>
                    </div>
                </div>
            )
        }
    }
}

const mapStateToProps = state => {
    return {
        uid: state.auth.uid,
        user: state.auth.user,
        inputID: state.content.inputID,
        inputID2: state.content.inputID2,
        activePageID: state.auth.activePageID,
        data: state.db.present.data && state.db.present.data.toJS(),
        created: state.auth.created,
        sessionId: state.content.sessionId
    }
};

const mapDispatchToProps = dispatch => {
    return {
        createItem: (item,data,uid,user,sessionId) => dispatch(actionCreators.createItem(item,data,uid,user,sessionId)),
        updateItem: (item,data,uid,sessionId) => dispatch(actionCreators.updateItem(item,data,uid,sessionId)),
        updateItemSchedule: (alerts, item, data, uid, sessionId) => dispatch(actionCreators.updateItemSchedule(alerts, item, data, uid, sessionId)),
        setInput: (inputID, inputID2) => dispatch(actionCreators.setInput(inputID, inputID2)),
        setModal: (element, id) => dispatch(actionCreators.setModal(element, id)),
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(ItemInput);

ItemInput.propTypes = {
    id: PropTypes.string,
    sectionID: PropTypes.string,
    pageID: PropTypes.string,
    parents: PropTypes.array,
    offset: PropTypes.number,
    type: PropTypes.oneOf(['Button', 'Input','Edit']),
};